import { useEffect, useMemo, useState } from 'react';
import Button from '@material-ui/core/Button';
import { PiCheckCircleDuotone } from 'react-icons/pi';

import PiWriterService from 'app/admin/piWriter/service/PiWriterService';

import OptimizationPipelinePopup, { OptimizationPipelinePopupHeader } from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelinePopup';

import OptimizationPipelineInputTable from 'app/admin/optimizationPipelineV2/component/pipeline/popups/base/OptimizationPipelineInputTable';

import { useOptimizationPipeline } from 'app/admin/optimizationPipelineV2/component/pipeline/providers/OptimizationPipelineProvider';

const columns = [
    {
        key: 'name',
        label: 'Attribute',
        readOnly: true,
        minWidth: 260,
        sortable: true
    },
    {
        key: 'value',
        label: 'Value',
        type: 'number',
        numeric: true,
        minWidth: 180
    }
];

function flatten(obj, prefix = '', out = {}) {
    if (!obj || typeof obj !== 'object') return out;

    Object.entries(obj).forEach(([key, val]) => {
        const next = prefix ? prefix + '.' + key : key;

        if (val && typeof val === 'object' && !Array.isArray(val)) {
            flatten(val, next, out);
        } else {
            out[next] = val;
        }
    });

    return out;
}

export default function ApproveSettingsPopup() {
    const { popup, closePopup, optimizationPipelineData, optimizationPipelineDetail, ts, getOnDemandResource, ensureOnDemandResource } = useOptimizationPipeline();

    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState([]);
    const [errors, setErrors] = useState({});

    const piWriterId = optimizationPipelineData?.piWriter;

    const defaults = useMemo(() => {
        return {
            best: flatten(optimizationPipelineDetail?.optimizer_best_state || {}),
            merged: flatten(optimizationPipelineDetail?.merged_data || {})
        };
    }, [optimizationPipelineDetail]);

    const inputAttributeNamesResource = getOnDemandResource('piWriterInputAttributeNames');

    useEffect(() => {
        if (popup?.type !== 'APPROVE_SETTINGS') return;
        if (!piWriterId) return;

        ensureOnDemandResource('piWriterInputAttributeNames');
    }, [popup, piWriterId, ensureOnDemandResource]);

    useEffect(() => {
        if (popup?.type !== 'APPROVE_SETTINGS') return;

        const names = Array.isArray(inputAttributeNamesResource?.data) ? inputAttributeNamesResource.data : [];

        setRows(
            names.map(name => ({
                id: name,
                name,
                value: defaults.best[name] ?? defaults.merged[name] ?? ''
            }))
        );
    }, [popup, inputAttributeNamesResource, defaults]);

    function handleChange(index, key, value) {
        setRows(prev => prev.map((row, i) => i === index ? { ...row, [key]: value } : row));

        setErrors(prev => {
            if (!prev?.[index]?.[key]) return prev;

            return {
                ...prev,
                [index]: {
                    ...prev[index],
                    [key]: ''
                }
            };
        });
    }

    async function handleSave() {
        const payload = {};
        const nextErrors = {};

        rows.forEach((row, i) => {
            if (row.value === '') {
                nextErrors[i] = { value: 'Required' };
                return;
            }

            const parsed = Number(row.value);

            if (Number.isNaN(parsed)) {
                nextErrors[i] = { value: 'Invalid number' };
                return;
            }

            payload[row.name] = parsed;
        });

        setErrors(nextErrors);

        if (Object.keys(nextErrors).length) {
            return;
        }

        setSaving(true);

        try {
            await PiWriterService.writeDataEntry(piWriterId, ts, payload);
            closePopup();
        }
        finally {
            setSaving(false);
        }
    }

    return (
        <OptimizationPipelinePopup
            open={popup?.type === 'APPROVE_SETTINGS'}
            onClose={closePopup}
            width={popup?.width || '480px'}
            minWidth={popup?.minWidth || 480}
            maxWidth={popup?.maxWidth || 1200}
            className={popup?.className}
        >

            <OptimizationPipelinePopupHeader
                title='Approve Settings'
                subtitle='Review and write PI Writer values'
                icon={PiCheckCircleDuotone}
                onClose={closePopup}
            />

            <OptimizationPipelineInputTable
                columns={columns}
                rows={rows}
                errors={errors}
                onChange={handleChange}
                loading={inputAttributeNamesResource.loading}
                error={inputAttributeNamesResource.error}
                minHeight={360}
                states={{
                    loadingTitle: 'Loading PI Writer input attributes...',
                    errorTitle: 'Failed to load PI Writer input attributes',
                    emptyTitle: piWriterId ? 'No PI Writer input attributes available' : 'PI Writer is not configured',
                    noResultsTitle: 'No matching PI Writer input attributes'
                }}
                sort={{
                    key: 'name',
                    direction: 'asc'
                }}
                rowKey={(row, index) => row?.id || row?.name || index}
            />

            <div className='shrink-0 flex items-center justify-between gap-12 px-16 py-12 border-t border-gray-200 bg-gray-50'>

                <Button onClick={closePopup} disabled={saving}>
                    Cancel
                </Button>

                <Button variant='contained' color='primary' onClick={handleSave} disabled={saving || inputAttributeNamesResource.loading}>
                    {saving ? 'Writing...' : 'Write Data'}
                </Button>

            </div>

        </OptimizationPipelinePopup>
    );
}